import { renderFlightMusicAsync, renderFlightSound, SOUND_DESIGN, type FlightMusic, type FlightSound } from "./flight-score";
export type { FlightMusic, FlightSound } from "./flight-score";
export interface FlightMusicState { track: FlightMusic; soft?: boolean }
// Structural native API, not DOM AudioParam: WeChat only guarantees writable `value`.
interface AudioGain { value: number; cancelScheduledValues?(time: number): void; setValueAtTime?(value: number, time: number): void; linearRampToValueAtTime?(value: number, time: number): void }
interface AudioOutputNode { connect(destination: AudioOutputNode): unknown; disconnect(): void }
interface AudioGainNode extends AudioOutputNode { gain: AudioGain }
interface AudioPCM { duration: number; getChannelData(channel: number): Float32Array; copyToChannel?(samples: Float32Array, channel: number, offset: number): void }
interface AudioSource extends AudioOutputNode { buffer: AudioPCM | null; loop: boolean; onended: ((...args: any[]) => void) | null; start(when?: number, offset?: number): void; stop(when?: number): void }
export interface FlightAudioContext { state: string; currentTime: number; sampleRate: number; destination: AudioOutputNode; resume(): Promise<unknown> | void; createGain(): AudioGainNode; createBuffer(channels: number, length: number, rate: number): AudioPCM; createBufferSource(): AudioSource }
/** One lazily unlocked context, separate buses, bounded voices and loop cache. */
export function flightAudio(create: () => FlightAudioContext | undefined) {
  let context: FlightAudioContext | undefined, effectsBus: AudioGainNode, musicBus: AudioGainNode;
  let soundOn = true, musicOn = true, wanted: FlightMusicState | null = null;
  let playing: { track: FlightMusic; source: AudioSource; gain: AudioGainNode; started: number; offset: number } | undefined;
  let fading: typeof playing;
  let duckUntil = 0, musicLevel = -1, generation = 0, resumePending = false;
  let resumeAttempt = 0, resumeAt = -Infinity;
  const last = new Map<FlightSound, number>(), variants = new Map<FlightSound, number>();
  const effects = new Map<string, AudioPCM>(), loops = new Map<FlightMusic, AudioPCM>();
  const pending = new Map<FlightMusic, Promise<void>>();
  const offsets = new Map<FlightMusic, number>();
  const voices: { source: AudioSource; priority: number }[] = [];
  const nativeRamps = new Map<AudioGainNode, { from: number; to: number; began: number; ends: number }>();
  let nativeTimer: ReturnType<typeof setTimeout> | undefined;
  let outputTimer: ReturnType<typeof setTimeout> | undefined;
  const nativeStops = new Map<AudioSource, { ends: number; finish: () => void }>();
  const reported = new Set<string>();
  function report(stage: string, error: unknown) {
    if (reported.has(stage)) return;
    reported.add(stage);
    console.warn("[flight-audio] " + stage, error instanceof Error ? error.message : String(error));
  }
  function pumpRamps() {
    nativeTimer = undefined;
    const time = context?.currentTime ?? 0;
    for (const [node, tween] of nativeRamps) {
      const progress = Math.min(1, Math.max(0, (time - tween.began) / (tween.ends - tween.began)));
      try { node.gain.value = progress >= 1 ? tween.to : tween.from + (tween.to - tween.from) * progress; }
      catch (error) { nativeRamps.delete(node); report("native gain", error); }
      if (progress >= 1) nativeRamps.delete(node);
    }
    if (nativeRamps.size) nativeTimer = setTimeout(pumpRamps, 16);
  }
  function ramp(node: AudioGainNode, value: number, seconds = .035) {
    const time = context!.currentTime, param = node.gain;
    if (param.cancelScheduledValues && param.setValueAtTime && param.linearRampToValueAtTime) {
      try {
        param.cancelScheduledValues(time); param.setValueAtTime(param.value, time);
        param.linearRampToValueAtTime(value, time + seconds);
        nativeRamps.delete(node); return;
      } catch (error) { report("gain automation fallback", error); }
    }
    // Native AudioParam has only `value`; do smooth gain changes on the audio clock.
    nativeRamps.set(node, { from: param.value, to: value, began: time, ends: time + seconds });
    if (nativeTimer === undefined) nativeTimer = setTimeout(pumpRamps, 16);
  }
  function copyPCM(buffer: AudioPCM, data: Float32Array, channel: number) {
    // Explicit native upload also works if getChannelData returns a copy across the bridge.
    if (buffer.copyToChannel) buffer.copyToChannel(data, channel, 0);
    else buffer.getChannelData(channel).set(data);
  }
  function pumpStops() {
    outputTimer = undefined;
    const now = context?.currentTime ?? 0;
    for (const [source, stop] of [...nativeStops]) if (now >= stop.ends) {
      nativeStops.delete(source); try { source.stop(); } catch {}
      stop.finish();
    }
    if (nativeStops.size) outputTimer = setTimeout(pumpStops, 40);
  }
  function expire(source: AudioSource, ends: number, finish: () => void) {
    nativeStops.set(source, { ends, finish });
    if (outputTimer === undefined) outputTimer = setTimeout(pumpStops, 40);
  }
  function ready(resume = false) {
    try {
      if (!context || context.state === "closed") {
        retire(playing); retire(fading); playing = fading = undefined; stopEffects();
        context = create();
        if (!context) return false;
        effectsBus = context.createGain(); musicBus = context.createGain();
        const master = context.createGain(); master.gain.value = .58;
        // Only use the small Web/WeChat common API surface; bounded mix has headroom without optional compressor APIs.
        effectsBus.gain.value = .85; musicBus.gain.value = 0;
        effectsBus.connect(master); musicBus.connect(master); master.connect(context.destination);
        effects.clear(); loops.clear(); pending.clear(); last.clear(); musicLevel = -1; duckUntil = 0;
      }
      if (context.state === "suspended" || (context.state as string) === "interrupted") {
        if (resume && (!resumePending || Date.now() - resumeAt > 750)) {
          resumePending = true;
          const token = generation, attempt = ++resumeAttempt; resumeAt = Date.now();
          Promise.resolve(context.resume()).then(() => { if (token === generation) sync(); }).catch((error) => report("context resume", error)).finally(() => { if (attempt === resumeAttempt) resumePending = false; });
        }
        return false;
      }
      return context.state === "running";
    } catch (error) { report("context initialization", error); context = undefined; return false; }
  }
  function disconnect(node: AudioOutputNode) { nativeRamps.delete(node as AudioGainNode); nativeStops.delete(node as AudioSource); try { node.disconnect(); } catch {} }
  function retire(voice: typeof playing) {
    if (!voice) return;
    try { voice.source.stop(); } catch {}
    disconnect(voice.source); disconnect(voice.gain);
  }
  function stopMusic(immediate: boolean) {
    if (!playing) { if (immediate) { retire(fading); fading = undefined; } return; }
    const old = playing; playing = undefined;
    const duration = old.source.buffer!.duration;
    offsets.set(old.track, (old.offset + Math.max(0, context!.currentTime - old.started)) % duration);
    retire(fading); fading = undefined;
    if (immediate) retire(old);
    else {
      fading = old; ramp(old.gain, 0, .4);
      old.source.onended = () => { disconnect(old.source); disconnect(old.gain); if (fading === old) fading = undefined; };
      expire(old.source, context!.currentTime + .42, () => {
        disconnect(old.source); disconnect(old.gain); if (fading === old) fading = undefined;
      });
      try { old.source.stop(context!.currentTime + .42); } catch { /* Timer fallback stops native sources after fading. */ }
    }
  }
  function stopEffects() {
    for (const voice of [...voices]) { try { voice.source.stop(); } catch {} disconnect(voice.source); }
    voices.length = 0;
  }
  function sync() {
    try {
      if (!musicOn || !wanted) { stopMusic(true); return; }
      if (!ready()) return;
      const level = (wanted.soft ? .16 : .44) * (context!.currentTime < duckUntil ? .38 : 1);
      if (level !== musicLevel) { ramp(musicBus, level, level > musicLevel ? .25 : .04); musicLevel = level; }
      if (playing?.track !== wanted.track) {
        const track = wanted.track;
        const buffer = loops.get(track);
        if (!buffer) {
          if (!pending.has(track)) {
            const output = context!;
            const job = renderFlightMusicAsync(track).then((pcm) => {
              if (context !== output || output.state === "closed") return;
              const decoded = output.createBuffer(2, pcm[0].length, 22050);
              pcm.forEach((samples, index) => copyPCM(decoded, samples, index));
              loops.set(track, decoded);
              if (loops.size > 3) loops.delete(loops.keys().next().value!);
              if (wanted && musicOn) sync();
            }).catch((error) => report("music buffer", error)).finally(() => { if (pending.get(track) === job) pending.delete(track); });
            pending.set(track, job);
          }
          return;
        }
        stopMusic(false);
        const source = context!.createBufferSource(), gain = context!.createGain();
        source.buffer = buffer; source.loop = true; gain.gain.value = 0;
        source.connect(gain); gain.connect(musicBus);
        const offset = offsets.get(wanted.track) ?? 0;
        playing = { track: wanted.track, source, gain, offset, started: context!.currentTime };
        source.start(0, offset); ramp(gain, 1, .4);
      }
    } catch (error) { report("music playback", error); stopMusic(true); }
  }
  const play = (kind: FlightSound) => {
    if (!soundOn) return;
    try {
      const cfg = SOUND_DESIGN[kind], now = Date.now();
      if (now - (last.get(kind) ?? -Infinity) < cfg[1] || !ready()) return;
      const priority = cfg[6], limit = priority >= 2 ? 8 : 6;
      if (voices.length >= limit) {
        const weakest = voices.find((v) => v.priority < priority);
        if (!weakest) return;
        try { weakest.source.stop(); } catch {}
        disconnect(weakest.source); const index = voices.indexOf(weakest); if (index >= 0) voices.splice(index, 1);
      }
      const variant = (variants.get(kind) ?? 0) % 3, key = kind + ":" + variant;
      let buffer = effects.get(key);
      if (!buffer) {
        const data = renderFlightSound(kind, context!.sampleRate, variant);
        buffer = context!.createBuffer(1, data.length, context!.sampleRate);
        copyPCM(buffer, data, 0); effects.set(key, buffer);
      }
      const source = context!.createBufferSource(), voice = { source, priority };
      source.buffer = buffer; source.connect(effectsBus); voices.push(voice);
      source.onended = () => { const i = voices.indexOf(voice); if (i >= 0) voices.splice(i, 1); disconnect(source); };
      expire(source, context!.currentTime + buffer.duration + .08, () => {
        const index = voices.indexOf(voice); if (index >= 0) voices.splice(index, 1); disconnect(source);
      });
      try { source.start(); } catch (e) { const index = voices.indexOf(voice); if (index >= 0) voices.splice(index, 1); disconnect(source); throw e; }
      last.set(kind, now); variants.set(kind, variant + 1);
      if (priority >= 2) { duckUntil = Math.max(duckUntil, context!.currentTime + Math.min(.85, cfg[0])); sync(); }
    } catch (error) { report("effect playback", error); /* Failures never interrupt combat. */ }
  };
  return Object.assign(play, {
    music(state: FlightMusicState | null) { wanted = state; sync(); },
    settings(settings: { sound: boolean; music: boolean }) {
      soundOn = settings.sound; musicOn = settings.music;
      if (!soundOn) stopEffects(); sync();
    },
    unlock() { if (ready(true)) sync(); },
    pause() {
      generation++; resumePending = false; wanted = null; stopEffects(); stopMusic(true);
      nativeRamps.clear(); nativeStops.clear();
      if (nativeTimer !== undefined) clearTimeout(nativeTimer);
      if (outputTimer !== undefined) clearTimeout(outputTimer);
      nativeTimer = outputTimer = undefined;
    },
  });
}
